import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE, hole, sende } from "./klient";

/* ══════════════════════════════════════════════════════════════════
   Der zweite Faktor — einrichten, entfernen, zurücksetzen
   (Entscheidung E4 vom 04.10.2026, Playbook A-06/E-06).

   Geprüft wurde der Code seit dem 03.10.2026; einrichten ließ sich die
   Authenticator-App in Machine Office nicht, und ein Konto mit
   Faktorpflicht kam nie hinein. Drei Wege:

   | Wer | Wo | Weg |
   |---|---|---|
   | jeder, angemeldet | Mein Konto | `GET/POST /ich/faktor…` |
   | wer verlangt ist und noch keinen hat | Anmeldung, Code-Schritt | `POST /anmeldung/faktor/einrichten`, dann `POST /anmeldung {…, faktor_id}` |
   | die Verwaltung | Benutzer und Rechte | `POST /benutzer/:id/faktor-zuruecksetzen` |
   ══════════════════════════════════════════════════════════════════ */

/** Was die App zum Einrichten braucht: Bild, Schlüssel, Kennung des Faktors. */
export type Faktoreinrichtung = {
  faktor_id: string;
  /** Ein SVG als `data:`-Adresse — so liefert es Supabase. */
  qr: string;
  /** Zum Abtippen, wenn die Kamera nicht will. */
  geheimnis: string;
};

export type Faktorstand = { verlangt: boolean; eingerichtet: boolean };

const ATTRAPPE_EINRICHTUNG: Faktoreinrichtung = {
  faktor_id: "attrappe",
  qr: "",
  geheimnis: "JBSWY3DPEHPK3PXP",
};

function alsEinrichtung(roh: Record<string, unknown>): Faktoreinrichtung {
  return {
    faktor_id: String(roh["faktor_id"] ?? ""),
    qr: String(roh["qr"] ?? ""),
    geheimnis: String(roh["geheimnis"] ?? ""),
  };
}

/** In der Anmeldung: Passwort noch einmal, dann das Bild (A-06). */
export async function faktorBeiAnmeldungEinrichten(email: string, passwort: string): Promise<Faktoreinrichtung> {
  if (ATTRAPPE) return ATTRAPPE_EINRICHTUNG;
  return alsEinrichtung(await sende<Record<string, unknown>>("/anmeldung/faktor/einrichten", { email, passwort }));
}

const SCHLUESSEL = ["ich", "faktor"] as const;

export const useMeinFaktor = () =>
  useQuery({
    queryKey: SCHLUESSEL,
    queryFn: async (): Promise<Faktorstand> => {
      if (ATTRAPPE) return { verlangt: false, eingerichtet: false };
      const roh = await hole<Record<string, unknown>>("/ich/faktor");
      return { verlangt: roh["verlangt"] === true, eingerichtet: roh["eingerichtet"] === true };
    },
  });

export function useFaktorEinrichten() {
  return useMutation({
    mutationFn: async (): Promise<Faktoreinrichtung> => (ATTRAPPE
      ? ATTRAPPE_EINRICHTUNG
      : alsEinrichtung(await sende<Record<string, unknown>>("/ich/faktor/einrichten", {}))),
  });
}

export function useFaktorBestaetigen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async ({ faktorId, code }: { faktorId: string; code: string }) => {
      if (ATTRAPPE) return;
      await sende<unknown>("/ich/faktor/bestaetigen", { faktor_id: faktorId, code });
    },
    onSuccess: () => void kunde.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

export function useFaktorEntfernen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      if (ATTRAPPE) return;
      await sende<unknown>("/ich/faktor/entfernen", { code });
    },
    onSuccess: () => void kunde.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

/** Die Verwaltung setzt den Faktor eines Menschen zurück. */
export function useFaktorZuruecksetzen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      if (ATTRAPPE) return;
      await sende<unknown>(`/benutzer/${encodeURIComponent(staffId)}/faktor-zuruecksetzen`, {});
    },
    onSuccess: () => void kunde.invalidateQueries({ queryKey: ["benutzer"] }),
  });
}

/** Sechs Ziffern, Leerzeichen erlaubt — dieselbe Regel wie in der API. */
export const codeGueltig = (code: string): boolean => /^\d{6}$/.test(code.replace(/\s/g, ""));
